import type { OfferedPlan } from '@/api/types';

export type Interval = 'month' | 'year';

export function money(cents: number) {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: cents % 100 ? 2 : 0,
  });
}

/** "R$ 49,90/mês", "R$ 499/ano (R$ 41,58/mês)", "Grátis". */
export function priceParts(plan: OfferedPlan, interval: Interval) {
  const month = plan.prices.month;
  const year = plan.prices.year;
  if (!month && !year) return { main: 'Grátis', unit: '', note: 'para sempre' };
  if (interval === 'year' && year) {
    const saving = month ? month * 12 - year : 0;
    return {
      main: money(Math.round(year / 12)),
      unit: '/mês',
      note: `${money(year)} por ano${saving > 0 ? ` · economize ${money(saving)}` : ''}`,
    };
  }
  return { main: money(month ?? 0), unit: '/mês', note: 'cobrado todo mês' };
}

const MODULES: Record<string, string> = {
  training: 'treinos',
  nutrition: 'nutrição',
  health: 'evolução',
  finance: 'finanças',
};

/** Plain-language bullets built from what the plan actually grants. */
export function planHighlights(plan: OfferedPlan): string[] {
  const out: string[] = [];
  const clients = plan.quotas['clients.active'];
  if (plan.kind === 'base') {
    out.push(clients == null ? 'Alunos ilimitados' : `Até ${clients} alunos ativos`);
    const modules = plan.modules.filter((m) => m in MODULES).map((m) => MODULES[m]);
    if (modules.length) {
      const last = modules.pop();
      out.push(
        `${modules.length ? `${modules.join(', ')} e ${last}` : last}`.replace(/^./, (c) =>
          c.toUpperCase(),
        ),
      );
    }
    out.push('Seu endereço, seu nome e suas cores');
    out.push('Arquivos no Google Drive de cada aluno');
    out.push('Mais módulos quando quiser: finanças grátis, IA à parte');
  }
  const photos = plan.quotas['ai.meal_photos'];
  const docs = plan.quotas['ai.documents'];
  if (photos) {
    out.push(`IA: ${photos} fotos de refeição${docs ? ` e ${docs} documentos` : ''} por mês`);
  }
  if (plan.kind === 'base' && plan.trialDays > 0) {
    out.push(`${plan.trialDays} dias para testar antes da 1ª fatura`);
  }
  return out;
}

export const RECOMMENDED = 'pro';
